// ResidentsPage — Resident Concerns register (BSA 2022 §92)
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Plus, Search, Loader2, X, Save, MessageSquare, UserX, ListChecks } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useView } from "@/context/ViewContext";
import Pill from "@/components/Pill";
import { useSortableRows, SortableTh } from "@/lib/sortable";

const PRIORITY_TONE = { Low: "neutral", Medium: "blue", High: "amber", Critical: "red" };
const STATUS_TONE = {
    Open: "amber", Acknowledged: "blue", Investigating: "blue",
    Resolved: "green", Closed: "neutral",
};
const CAN_EDIT_ROLES = new Set(["director", "administrator", "consultant", "client_admin", "site_manager"]);

const inputCls = "w-full h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm focus:outline-none focus:border-[var(--brand-accent,_#C75A1B)] disabled:bg-[#F6F4F0]";

function Field({ label, children }) {
    return (
        <div>
            <label className="block text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] mb-1">{label}</label>
            {children}
        </div>
    );
}

export default function ResidentsPage() {
    const [rows, setRows] = useState([]);
    const [clients, setClients] = useState([]);
    const [buildings, setBuildings] = useState([]);
    const [meta, setMeta] = useState({ concern_types: [], priorities: [], statuses: [] });
    const [q, setQ] = useState("");
    const [statusFilter, setStatusFilter] = useState("All");
    const [priorityFilter, setPriorityFilter] = useState("All");
    const [portalVis, setPortalVis] = useState("");
    const [criticalVal, setCriticalVal] = useState("");
    const [projectFilter, setProjectFilter] = useState("");
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialog, setDialog] = useState(null);
    const { user } = useAuth();
    const { portalClientId, isInternalView } = useView();
    const canEdit = user && CAN_EDIT_ROLES.has(user.role);
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const projectId = params.get("project") || "";
    const [projectInfo, setProjectInfo] = useState(null);

    useEffect(() => {
        if (!projectId) { setProjectInfo(null); return; }
        api.get(`/projects/${projectId}`).then((r) => setProjectInfo(r.data)).catch(() => setProjectInfo(null));
    }, [projectId]);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (portalClientId && !isInternalView) params.client_id = portalClientId;
            if (projectId) params.project_id = projectId;
            if (statusFilter !== "All") params.status = statusFilter;
            if (priorityFilter !== "All") params.priority = priorityFilter;
            const { data } = await api.get("/residents", { params });
            setRows(data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not load concerns");
        }
        setLoading(false);
    }, [portalClientId, isInternalView, statusFilter, priorityFilter, projectId]);

    useEffect(() => { refresh(); }, [refresh]);
    useEffect(() => {
        api.get("/residents/meta").then((r) => setMeta(r.data)).catch(() => { });
        api.get("/clients").then((r) => setClients(r.data)).catch(() => { });
        api.get("/buildings").then((r) => setBuildings(r.data)).catch(() => { });
        api.get("/projects").then((r) => setProjects(r.data)).catch(() => { });
    }, []);

    const clientMap = useMemo(() => Object.fromEntries(clients.map((c) => [c.id, c])), [clients]);
    const buildingMap = useMemo(() => Object.fromEntries(buildings.map((b) => [b.id, b])), [buildings]);

    const filtered = rows.filter((r) => {
        if (portalVis === "yes" && !r.portal_visible) return false;
        if (portalVis === "no" && r.portal_visible) return false;
        if (criticalVal === "yes" && !r.critical) return false;
        if (criticalVal === "no" && r.critical) return false;
        if (projectFilter && r.project_id !== projectFilter) return false;
        if (!q) return true;
        const hay = `${r.reference || ""} ${r.description || ""} ${r.raised_by_name || ""} ${clientMap[r.client_id]?.name || ""}`.toLowerCase();
        return hay.includes(q.toLowerCase());
    });
    const { sortKey, sortDir, toggleSort, sorted } = useSortableRows(filtered, {
        defaultKey: "created_at", defaultDir: "desc",
        accessors: {
            reference: (r) => r.reference || "",
            raised_by: (r) => r.anonymous ? "zzz-anon" : (r.raised_by_name || ""),
            client: (r) => clientMap[r.client_id]?.name || "",
            description: (r) => r.description || "",
            priority: (r) => ({ Low: 1, Medium: 2, High: 3, Critical: 4 })[r.priority] || 0,
            status: (r) => r.status || "",
            created_at: (r) => r.created_at || "",
        },
    });

    return (
        <div data-testid="residents-page" className="space-y-5">
            {projectId && (
                <div data-testid="residents-project-banner" className="flex items-center justify-between gap-3 flex-wrap rounded-[10px] border px-4 py-2.5" style={{ borderColor: "#E7C9A8", background: "#FBF1E6" }}>
                    <div className="text-[12px] font-semibold" style={{ color: "#8A5A1E" }}>Filtered to project: {projectInfo?.name || projectId}</div>
                    <button data-testid="residents-back-to-project" onClick={() => navigate(`/app/projects/${projectId}`)}
                        className="inline-flex items-center gap-1 text-[11.5px] font-semibold px-2.5 py-1.5 rounded-[7px] bg-white border" style={{ borderColor: "#E7C9A8", color: "#8A5A1E" }}>← Back to project</button>
                </div>
            )}
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <span className="inline-block pb-1 text-[10.5px] font-bold tracking-[.08em] uppercase"
                        style={{ borderBottom: "2px solid var(--brand-accent, #C75A1B)", color: "#6F6A64" }}>
                        Safety &amp; Compliance · BSA §92
                    </span>
                    <h2 className="text-2xl font-bold tracking-tight mt-2 text-[#1F1F1F]">Resident Engagement</h2>
                    <p className="text-[13px] mt-1" style={{ color: "#6F6A64" }}>
                        {rows.length} concerns logged ·{" "}
                        <span className="font-semibold text-[#C68A1C]">
                            {rows.filter((r) => !["Resolved", "Closed"].includes(r.status)).length} open
                        </span>{" · "}
                        <span className="font-semibold text-red-600">
                            {rows.filter((r) => r.priority === "Critical" && r.status !== "Resolved" && r.status !== "Closed").length} critical
                        </span>
                    </p>
                </div>
                {canEdit && (
                    <button data-testid="resident-new-btn" onClick={() => setDialog("new")}
                        className="inline-flex items-center gap-2 text-sm font-semibold px-3.5 py-2 rounded-lg text-white"
                        style={{ background: "var(--brand-accent, #C75A1B)" }}>
                        <Plus size={15} /> Log concern
                    </button>
                )}
            </div>

            <div className="flex items-center gap-3 flex-wrap">
                <div className="relative">
                    <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9A968F]" />
                    <input data-testid="resident-search" placeholder="Search reference, description, name…"
                        value={q} onChange={(e) => setQ(e.target.value)}
                        className="h-9 pl-8 pr-3 rounded-lg border border-[#E5E0DA] bg-white text-sm w-80 focus:outline-none focus:border-[var(--brand-accent,_#C75A1B)]" />
                </div>
                <select data-testid="resident-status-filter" value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)} className="h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm">
                    <option value="All">All statuses</option>
                    {meta.statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <select data-testid="resident-priority-filter" value={priorityFilter}
                    onChange={(e) => setPriorityFilter(e.target.value)} className="h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm">
                    <option value="All">All priorities</option>
                    {meta.priorities.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
                {isInternalView && (
                    <>
                        <select data-testid="resident-project-filter" value={projectFilter} onChange={(e) => setProjectFilter(e.target.value)} className="h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm">
                            <option value="">All projects</option>
                            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                        <select data-testid="resident-portalvis-filter" value={portalVis} onChange={(e) => setPortalVis(e.target.value)} className="h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm">
                            <option value="">Any visibility</option>
                            <option value="yes">Portal-visible</option>
                            <option value="no">Internal only</option>
                        </select>
                        <select data-testid="resident-critical-filter" value={criticalVal} onChange={(e) => setCriticalVal(e.target.value)} className="h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm">
                            <option value="">Any criticality</option>
                            <option value="yes">Critical only</option>
                            <option value="no">Non-critical</option>
                        </select>
                    </>
                )}
            </div>

            <div className="rounded-2xl bg-white border border-[#E5E0DA] overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-[#F5F3F0] text-left border-b border-[#E5E0DA] group">
                        <tr>
                            <SortableTh sortKey={sortKey} sortDir={sortDir} myKey="reference" onClick={toggleSort}>Ref / Type</SortableTh>
                            <SortableTh sortKey={sortKey} sortDir={sortDir} myKey="raised_by" onClick={toggleSort}>Raised by</SortableTh>
                            <SortableTh sortKey={sortKey} sortDir={sortDir} myKey="client" onClick={toggleSort}>Client / Building</SortableTh>
                            <SortableTh sortKey={sortKey} sortDir={sortDir} myKey="description" onClick={toggleSort}>Description</SortableTh>
                            <SortableTh sortKey={sortKey} sortDir={sortDir} myKey="priority" onClick={toggleSort}>Priority</SortableTh>
                            <SortableTh sortKey={sortKey} sortDir={sortDir} myKey="status" onClick={toggleSort}>Status</SortableTh>
                            <SortableTh sortKey={sortKey} sortDir={sortDir} myKey="created_at" onClick={toggleSort}>Date Raised</SortableTh>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={7} className="text-center py-10 text-[#9A968F]">
                                <Loader2 size={16} className="inline animate-spin mr-2" />Loading…</td></tr>
                        ) : sorted.length === 0 ? (
                            <tr><td colSpan={7} className="text-center py-10 text-[#9A968F]">No concerns match.</td></tr>
                        ) : sorted.map((r) => (
                            <tr key={r.id} data-testid={`resident-row-${r.id}`}
                                className="border-b border-[#F0EDE8] hover:bg-[#F5F3F0] cursor-pointer"
                                onClick={() => setDialog(r)}>
                                <td className="px-4 py-3">
                                    <div className="font-mono text-[12px] text-[#9A968F]">{r.reference}</div>
                                    <div className="font-semibold text-[#1F1F1F]">{r.concern_type}</div>
                                </td>
                                <td className="px-4 py-3 text-[#6F6A64] text-[12px]">
                                    {r.anonymous ? (
                                        <span className="inline-flex items-center gap-1 text-[#9A968F]"><UserX size={11} /> Anonymous</span>
                                    ) : (r.raised_by_name || "—")}
                                    {r.raised_by_unit && <div className="text-[11px]">{r.raised_by_unit}</div>}
                                </td>
                                <td className="px-4 py-3 text-[#6F6A64] text-[12px]">
                                    <div>{clientMap[r.client_id]?.name || "—"}</div>
                                    {r.building_id && <div className="text-[11px]">{buildingMap[r.building_id]?.name || ""}</div>}
                                </td>
                                <td className="px-4 py-3 text-[#6F6A64] text-[12px] max-w-md truncate">{r.description}</td>
                                <td className="px-4 py-3"><Pill tone={PRIORITY_TONE[r.priority] || "neutral"}>{r.priority}</Pill></td>
                                <td className="px-4 py-3"><Pill tone={STATUS_TONE[r.status] || "neutral"}>{r.status}</Pill></td>
                                <td className="px-4 py-3 text-[#6F6A64] text-[12px] font-mono whitespace-nowrap">{(r.created_at || "").slice(0, 10) || "—"}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {dialog && (
                <ConcernDialog
                    concern={dialog === "new" ? null : dialog}
                    meta={meta} clients={clients} buildings={buildings}
                    portalClientId={!isInternalView ? portalClientId : null}
                    onClose={() => setDialog(null)}
                    onSaved={() => { setDialog(null); refresh(); }}
                />
            )}
        </div>
    );
}

function ConcernDialog({ concern, meta, clients, buildings, portalClientId, onClose, onSaved }) {
    const isNew = !concern;
    const [form, setForm] = useState(() => concern ? { ...concern } : {
        client_id: portalClientId || clients[0]?.id || "",
        building_id: "",
        concern_type: "Other",
        priority: "Medium",
        anonymous: false,
        raised_by_name: "",
        raised_by_email: "",
        raised_by_phone: "",
        raised_by_unit: "",
        description: "",
        target_response_date: "",
        status: "Open",
        response_summary: "",
    });
    const [saving, setSaving] = useState(false);
    const [actionTitle, setActionTitle] = useState("");
    const filteredBuildings = buildings.filter((b) => !form.client_id || b.client_id === form.client_id);

    async function save() {
        if (!form.client_id) { toast.error("Client required"); return; }
        if (!(form.description || "").trim()) { toast.error("Description required"); return; }
        setSaving(true);
        try {
            const payload = { ...form };
            ["raised_by_email", "raised_by_phone", "raised_by_unit", "raised_by_name", "target_response_date", "response_summary", "building_id"].forEach((k) => {
                if (!payload[k]) delete payload[k];
            });
            if (isNew) {
                await api.post("/residents", payload);
                toast.success("Concern logged");
            } else {
                await api.patch(`/residents/${concern.id}`, payload);
                toast.success("Concern updated");
            }
            onSaved();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
        }
        setSaving(false);
    }

    async function createLinkedAction() {
        if (!actionTitle.trim()) { toast.error("Action title required"); return; }
        setSaving(true);
        try {
            await api.post(`/residents/${concern.id}/create-action`, { title: actionTitle, priority: "High" });
            toast.success("Linked action created");
            setActionTitle("");
            onSaved();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not create action");
        }
        setSaving(false);
    }

    return (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" data-testid="resident-dialog">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[92vh]">
                <div className="px-5 py-4 border-b border-[#E5E0DA] flex items-start justify-between">
                    <div>
                        <h3 className="text-base font-bold text-[#1F1F1F] flex items-center gap-2">
                            <MessageSquare size={15} />
                            {isNew ? "Log resident concern" : `Concern ${concern.reference}`}
                        </h3>
                        {!isNew && <div className="text-[11px] text-[#9A968F] mt-1">Raised {(concern.created_at || "").slice(0, 10)}</div>}
                    </div>
                    <button onClick={onClose} className="p-1 text-[#9A968F] hover:text-[#1F1F1F]"><X size={16} /></button>
                </div>
                <div className="p-5 space-y-3 overflow-y-auto">
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Client *">
                            <select data-testid="resident-client" value={form.client_id}
                                onChange={(e) => setForm({ ...form, client_id: e.target.value, building_id: "" })}
                                disabled={!!portalClientId || !isNew} className={inputCls}>
                                <option value="">— Select —</option>
                                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                        </Field>
                        <Field label="Building (optional)">
                            <select data-testid="resident-building" value={form.building_id || ""}
                                onChange={(e) => setForm({ ...form, building_id: e.target.value })} className={inputCls}>
                                <option value="">— None —</option>
                                {filteredBuildings.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                            </select>
                        </Field>
                        <Field label="Concern type *">
                            <select data-testid="resident-type" value={form.concern_type}
                                onChange={(e) => setForm({ ...form, concern_type: e.target.value })} className={inputCls}>
                                {meta.concern_types.map((t) => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </Field>
                        <Field label="Priority *">
                            <select data-testid="resident-priority" value={form.priority}
                                onChange={(e) => setForm({ ...form, priority: e.target.value })} className={inputCls}>
                                {meta.priorities.map((p) => <option key={p} value={p}>{p}</option>)}
                            </select>
                        </Field>
                    </div>

                    <label className="flex items-center gap-2 text-[13px] text-[#1F1F1F]">
                        <input data-testid="resident-anonymous" type="checkbox" checked={!!form.anonymous}
                            onChange={(e) => setForm({ ...form, anonymous: e.target.checked })} />
                        Anonymous resident
                    </label>

                    {!form.anonymous && (
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Resident name">
                                <input data-testid="resident-name" value={form.raised_by_name || ""}
                                    onChange={(e) => setForm({ ...form, raised_by_name: e.target.value })} className={inputCls} />
                            </Field>
                            <Field label="Flat / unit">
                                <input value={form.raised_by_unit || ""}
                                    onChange={(e) => setForm({ ...form, raised_by_unit: e.target.value })}
                                    placeholder="e.g. Flat 14C" className={inputCls} />
                            </Field>
                            <Field label="Email">
                                <input type="email" value={form.raised_by_email || ""}
                                    onChange={(e) => setForm({ ...form, raised_by_email: e.target.value })} className={inputCls} />
                            </Field>
                            <Field label="Phone">
                                <input value={form.raised_by_phone || ""}
                                    onChange={(e) => setForm({ ...form, raised_by_phone: e.target.value })} className={inputCls} />
                            </Field>
                        </div>
                    )}

                    <Field label="Description *">
                        <textarea data-testid="resident-description" rows={3} value={form.description || ""}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                            className={inputCls + " h-auto py-2"} />
                    </Field>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Target response by">
                            <input type="date" value={form.target_response_date || ""}
                                onChange={(e) => setForm({ ...form, target_response_date: e.target.value })} className={inputCls} />
                        </Field>
                        <Field label="Status">
                            <select data-testid="resident-status" value={form.status}
                                onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputCls}>
                                {meta.statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </Field>
                    </div>
                    {!isNew && (
                        <Field label="Response summary / investigation notes">
                            <textarea rows={2} value={form.response_summary || ""}
                                onChange={(e) => setForm({ ...form, response_summary: e.target.value })}
                                className={inputCls + " h-auto py-2"} />
                        </Field>
                    )}

                    {!isNew && (
                        <div className="border-t border-[#E5E0DA] pt-4 mt-4">
                            <h4 className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] mb-2 flex items-center gap-1">
                                <ListChecks size={12} /> Create linked action
                            </h4>
                            <div className="flex items-center gap-2">
                                <input data-testid="resident-action-title"
                                    placeholder="e.g. Inspect smoke vent on level 22"
                                    value={actionTitle} onChange={(e) => setActionTitle(e.target.value)}
                                    className="h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm flex-1" />
                                <button data-testid="resident-action-create" disabled={saving || !actionTitle.trim()}
                                    onClick={createLinkedAction}
                                    className="inline-flex items-center gap-1 text-sm font-semibold px-3 py-2 rounded-lg text-white disabled:opacity-50"
                                    style={{ background: "#2A5DB0" }}>
                                    <Plus size={13} /> Create
                                </button>
                            </div>
                        </div>
                    )}
                </div>
                <div className="px-5 py-3 border-t border-[#E5E0DA] flex items-center justify-end gap-2 bg-[#F5F3F0]">
                    <button onClick={onClose} className="px-3 py-2 text-sm text-[#6F6A64] hover:text-[#1F1F1F]">Cancel</button>
                    <button data-testid="resident-save" onClick={save} disabled={saving}
                        className="inline-flex items-center gap-2 text-sm font-semibold px-3.5 py-2 rounded-lg text-white disabled:opacity-60"
                        style={{ background: "var(--brand-accent, #C75A1B)" }}>
                        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {isNew ? "Log concern" : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
}
